
/**
 * ELAKAY – GraffitiConfigurator
 * 
 * Composant fluide (PAS de modal/backdrop/portal).
 * S'intègre dans n'importe quel container parent.
 *
 * Usage :
 *   <GraffitiConfigurator
 *     productID={product._id}
 *     authToken={token}
 *     sellerSlots={product.sellerSlots}
 *     ImagesProd={product}
 *     product={product}
 *     onAddToCart={data => ...}
 *     initialGraphicInfo={savedDraft}
 *   />
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";

import useGraphicOrderSave from "./externPageGraffiti/useGraphicOrderSave";

import {
  DesignsList,
  DesignPreviewModal,
  parseLoadedDesign
} from "./externPageGraffiti/GraffitiPreview";

import DesignStudio, {
  makeSlotDesign,
  CSS_
} from "./externPageGraffiti/DesignStudio";

import ShowimagesLoading from "../showImagesLoading";
import { elakayAutoText } from "../dataElakay";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["graffitiConfigurator"]?.[key] || fallback;

/* ══════════════════════════════════════════════════════

   CSS — épuré, sans modal
══════════════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&family=Space+Mono:wght@400;700&display=swap');

*,*::before,*::after{box-sizing:border-box}

.gc-root{
  --n0:#f2f6fd;
  --n1:#ffffff;
  --n2:#f4f8fc;
  --n3:#eef6fb;
  --n4:#daeaf5;
  --n5:#c8ddea;
  --primary:#007399;
  --primary-light:#00a4d8;
  --primary-soft:rgba(0,115,153,.10);
  --primary-softer:rgba(0,115,153,.05);
  --teal:#4facfe;
  --teal-light:#00f2fe;
  --teal-soft:rgba(79,172,254,.10);
  --emerald:#34c759;
  --emerald-soft:rgba(52,199,89,.08);
  --err:#ef4444;
  --err-soft:rgba(239,68,68,.08);
  --text:#0d2d3d;
  --text-muted:#5a7a8a;
  --text-mutest:#8aa6b5;
  --b:rgba(0,115,153,.10);
  --b2:rgba(0,115,153,.20);
  --r:10px;
  --rL:14px;
  --tr:.18s ease;
  --ff-display:'Bebas Neue',sans-serif;
  --ff-body:'DM Sans',sans-serif;
  --ff-mono:'Space Mono',monospace;

  font-family:var(--ff-body);
  color:var(--text);
  background:var(--n1);
  width:100%;
  display:flex;
  flex-direction:column;
  border-radius:var(--rL);
  overflow:hidden
}

/* ══════════════════════════════════════════════════════
   MODE SWITCHER
══════════════════════════════════════════════════════ */
.gc-modes{
  display:grid;
  grid-template-columns:repeat(3,1fr);
  border-bottom:1.5px solid var(--n4);
  background:var(--n2);
  flex-shrink:0
}
.gc-mode{
  position:relative;
  padding:16px 14px;
  background:transparent;
  border:none;
  border-right:1px solid var(--n4);
  color:var(--text-muted);
  cursor:pointer;
  font-family:var(--ff-body);
  font-size:12px;
  font-weight:600;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  transition:all var(--tr);
  letter-spacing:.3px
}
.gc-mode:last-child{border-right:none}
.gc-mode::after{
  content:'';
  position:absolute;
  bottom:-1.5px;
  left:0;right:0;
  height:3px;
  background:linear-gradient(90deg,var(--primary),var(--primary-light));
  transform:scaleX(0);
  transition:transform var(--tr)
}
.gc-mode.on{color:var(--primary);background:var(--n1);font-weight:700}
.gc-mode.on::after{transform:scaleX(1)}
.gc-mode:hover:not(.on){color:var(--text);background:var(--n3)}
.gc-mode-num{
  font-family:var(--ff-mono);
  font-size:10px;
  font-weight:700;
  color:var(--text-mutest);
  background:var(--n4);
  border-radius:4px;
  padding:3px 7px;
  transition:all var(--tr)
}
.gc-mode.on .gc-mode-num{color:var(--primary);background:var(--primary-soft)}
.gc-mode-icon{font-size:15px;line-height:1}

/* ══════════════════════════════════════════════════════
   STATUS BAR
══════════════════════════════════════════════════════ */
.gc-status{
  padding:10px 20px;
  border-bottom:1px solid var(--n4);
  display:flex;
  align-items:center;
  gap:10px;
  font-size:12px;
  flex-wrap:wrap
}
.gc-status.busy{background:var(--primary-softer);color:var(--primary)}
.gc-status.draft{background:var(--teal-soft);color:var(--teal)}
.gc-status.submitted{background:var(--emerald-soft);color:var(--emerald)}
.gc-status.error{background:var(--err-soft);color:var(--err)}
.gc-status-icon{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:24px;height:24px;
  border-radius:50%;
  font-size:12px;
  flex-shrink:0
}
.gc-status.busy .gc-status-icon{background:var(--primary-soft);animation:gc-pulse 1.4s ease infinite}
.gc-status.draft .gc-status-icon{background:var(--teal-soft)}
.gc-status.submitted .gc-status-icon{background:var(--emerald-soft)}
.gc-status.error .gc-status-icon{background:var(--err-soft)}
.gc-status-main{font-weight:600;flex:1;min-width:0}
.gc-status-sub{font-size:11px;opacity:.75}
.gc-status-close{
  background:transparent;
  border:none;
  color:currentColor;
  cursor:pointer;
  font-size:14px;
  opacity:.6;
  padding:2px 6px;
  border-radius:4px;
  transition:all var(--tr)
}
.gc-status-close:hover{opacity:1;background:rgba(0,0,0,.05)}
@keyframes gc-pulse{0%,100%{opacity:1}50%{opacity:.5}}

/* ══════════════════════════════════════════════════════
   BODY
══════════════════════════════════════════════════════ */
.gc-body{
  flex:1;
  padding:22px;
  background:var(--n0);
  overflow-y:auto;
  min-height:400px
}

/* ══════════════════════════════════════════════════════
   CARDS
══════════════════════════════════════════════════════ */
.gc-card{
  background:var(--n1);
  border:1px solid var(--n4);
  border-radius:var(--rL);
  overflow:hidden;
  margin-bottom:16px;
  transition:all var(--tr)
}
.gc-card:hover{border-color:var(--b2);box-shadow:0 6px 20px rgba(0,40,60,.05)}
.gc-card-head{
  display:flex;
  align-items:center;
  gap:10px;
  padding:14px 18px;
  border-bottom:1px solid var(--n4);
  background:linear-gradient(180deg,var(--primary-softer),transparent)
}
.gc-card-icon{font-size:18px;flex-shrink:0}
.gc-card-title{
  font-size:14px;
  font-weight:700;
  color:var(--text);
  flex:1;
  letter-spacing:.2px
}
.gc-card-body{
  padding:18px;
  display:flex;
  flex-direction:column;
  gap:14px
}

/* ══════════════════════════════════════════════════════
   FIELDS
══════════════════════════════════════════════════════ */
.gc-field{display:flex;flex-direction:column;gap:6px}
.gc-label{
  font-size:10px;
  font-weight:700;
  color:var(--text-muted);
  text-transform:uppercase;
  letter-spacing:.8px
}
.gc-label.avoid{color:var(--err)}
.gc-label.required{color:var(--emerald)}
.gc-input,.gc-select,.gc-textarea{
  width:100%;
  background:var(--n1);
  border:1.5px solid var(--n4);
  border-radius:8px;
  padding:9px 12px;
  font-size:13px;
  color:var(--text);
  font-family:var(--ff-body);
  outline:none;
  transition:all var(--tr)
}
.gc-input:focus,.gc-select:focus,.gc-textarea:focus{
  border-color:var(--primary);
  box-shadow:0 0 0 3px var(--primary-soft)
}
.gc-textarea{resize:vertical;min-height:80px}
.gc-textarea.avoid{border-color:rgba(239,68,68,.3)}
.gc-textarea.required{border-color:rgba(52,199,89,.3)}
.gc-select{
  appearance:none;
  cursor:pointer;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23007399' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat:no-repeat;
  background-position:right 12px center;
  padding-right:32px
}
.gc-row-2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
@media(max-width:600px){.gc-row-2{grid-template-columns:1fr}}

/* ══════════════════════════════════════════════════════
   CHIPS
══════════════════════════════════════════════════════ */
.gc-chips{display:flex;flex-wrap:wrap;gap:6px}
.gc-chip{
  padding:6px 13px;
  border-radius:18px;
  border:1.5px solid var(--n4);
  background:var(--n1);
  color:var(--text-muted);
  font-size:11px;
  font-weight:500;
  cursor:pointer;
  font-family:var(--ff-body);
  transition:all var(--tr);
  letter-spacing:.2px
}
.gc-chip:hover{
  border-color:var(--primary);
  color:var(--text);
  transform:translateY(-1px)
}
.gc-chip.on{
  border-color:var(--primary);
  background:var(--primary-soft);
  color:var(--primary);
  font-weight:700
}

/* ══════════════════════════════════════════════════════
   MODE 2 — ASK SELLER
══════════════════════════════════════════════════════ */
.gc-mode-ask{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:20px;
  align-items:start
}
@media(max-width:900px){.gc-mode-ask{grid-template-columns:1fr}}
.gc-mode-ask-col{display:flex;flex-direction:column;gap:16px}

.gc-inspiration-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.gc-inspiration-card{
  padding:14px;
  background:var(--n2);
  border:1.5px solid var(--n4);
  border-radius:var(--r);
  cursor:pointer;
  display:flex;
  gap:12px;
  align-items:center;
  transition:all var(--tr)
}
.gc-inspiration-card:hover{
  border-color:var(--primary);
  background:var(--primary-softer);
  transform:translateY(-2px);
  box-shadow:0 4px 12px rgba(0,40,60,.06)
}
.gc-inspiration-card.on{
  border-color:var(--primary);
  background:var(--primary-soft);
  box-shadow:0 4px 14px rgba(0,115,153,.12)
}
.gc-inspiration-icon{
  font-size:28px;
  flex-shrink:0;
  width:42px;height:42px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:var(--n1);
  border-radius:10px;
  border:1px solid var(--n4)
}
.gc-inspiration-card.on .gc-inspiration-icon{
  background:var(--primary-soft);
  border-color:var(--primary)
}
.gc-inspiration-info{flex:1;min-width:0}
.gc-inspiration-name{
  font-size:13px;
  font-weight:700;
  color:var(--text);
  margin-bottom:3px
}
.gc-inspiration-card.on .gc-inspiration-name{color:var(--primary)}
.gc-inspiration-desc{
  font-size:11px;
  color:var(--text-muted);
  line-height:1.4
}
.gc-inspiration-check{
  margin-left:auto;
  color:var(--primary);
  font-size:18px;
  font-weight:700
}

.gc-ref-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
.gc-ref-slot{
  aspect-ratio:4/3;
  border-radius:var(--r);
  background:var(--n2);
  border:1.5px dashed var(--b2);
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:8px;
  cursor:pointer;
  position:relative;
  transition:all var(--tr);
  overflow:hidden;
  padding:12px
}
.gc-ref-slot:hover{
  border-color:var(--primary);
  background:var(--primary-softer)
}
.gc-ref-slot.filled{
  border-style:solid;
  border-color:var(--primary);
  background:var(--n1);
  padding:0
}
.gc-ref-slot img{
  width:100%;height:100%;
  object-fit:cover;
  border-radius:calc(var(--r) - 2px)
}
.gc-ref-empty-icon{
  font-size:28px;
  color:var(--text-mutest);
  font-weight:300
}
.gc-ref-empty-label{
  font-size:11px;
  color:var(--text-muted);
  font-weight:500
}
.gc-ref-remove{
  position:absolute;
  top:6px;right:6px;
  width:22px;height:22px;
  background:rgba(239,68,68,.92);
  border:none;
  border-radius:50%;
  color:#fff;
  font-size:11px;
  cursor:pointer;
  z-index:2;
  display:flex;
  align-items:center;
  justify-content:center;
  transition:all var(--tr);
  backdrop-filter:blur(4px)
}
.gc-ref-remove:hover{background:var(--err);transform:scale(1.1)}

.gc-summary-row{
  display:flex;
  justify-content:space-between;
  align-items:center;
  padding:8px 0;
  border-bottom:1px solid var(--n4);
  font-size:12px
}
.gc-summary-row:last-child{border-bottom:none}
.gc-summary-key{color:var(--text-muted);font-weight:500}
.gc-summary-value{
  color:var(--text);
  font-weight:600;
  max-width:60%;
  text-align:right;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap
}
.gc-info-banner{
  padding:10px 14px;
  background:var(--teal-soft);
  border:1px solid rgba(79,172,254,.25);
  border-radius:8px;
  font-size:11.5px;
  color:var(--teal);
  line-height:1.5;
  margin-top:8px;
  display:flex;
  align-items:center;
  gap:8px
}

/* ══════════════════════════════════════════════════════
   MODE 3 — TEMPLATE BANNER
══════════════════════════════════════════════════════ */
.gc-template-banner{
  display:flex;
  align-items:center;
  gap:14px;
  padding:14px 18px;
  margin-bottom:16px;
  background:linear-gradient(135deg,var(--teal-soft),var(--primary-softer));
  border:1.5px solid rgba(79,172,254,.25);
  border-radius:var(--rL);
  flex-wrap:wrap
}
.gc-template-banner-icon{
  width:42px;height:42px;
  border-radius:10px;
  background:rgba(79,172,254,.18);
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:22px;
  flex-shrink:0
}
.gc-template-banner-info{flex:1;min-width:180px}
.gc-template-banner-title{
  font-size:13px;
  font-weight:700;
  color:var(--teal);
  margin-bottom:3px
}
.gc-template-banner-sub{
  font-size:11px;
  color:var(--text-muted);
  line-height:1.5
}

/* ══════════════════════════════════════════════════════
   BUTTONS
══════════════════════════════════════════════════════ */
.gc-btn{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  padding:9px 18px;
  border-radius:8px;
  font-family:var(--ff-body);
  font-size:12px;
  font-weight:600;
  cursor:pointer;
  border:none;
  transition:all var(--tr);
  letter-spacing:.4px;
  text-transform:uppercase;
  white-space:nowrap
}
.gc-btn:disabled{opacity:.45;cursor:not-allowed;pointer-events:none}
.gc-btn-primary{
  background:linear-gradient(135deg,var(--primary),var(--primary-light));
  color:#fff;
  box-shadow:0 4px 14px rgba(0,115,153,.28)
}
.gc-btn-primary:hover:not(:disabled){
  filter:brightness(1.08);
  transform:translateY(-1px);
  box-shadow:0 6px 20px rgba(0,115,153,.38)
}
.gc-btn-success{
  background:linear-gradient(135deg,var(--emerald),#4ade80);
  color:#fff;
  box-shadow:0 4px 14px rgba(52,199,89,.28)
}
.gc-btn-success:hover:not(:disabled){
  filter:brightness(1.08);
  transform:translateY(-1px)
}
.gc-btn-secondary{
  background:var(--n2);
  color:var(--text);
  border:1.5px solid var(--n4)
}
.gc-btn-secondary:hover:not(:disabled){
  background:var(--n3);
  border-color:var(--primary)
}
.gc-btn-ghost{
  background:transparent;
  color:var(--text-muted);
  border:1.5px solid var(--n4)
}
.gc-btn-ghost:hover:not(:disabled){
  color:var(--primary);
  border-color:var(--primary);
  background:var(--primary-softer)
}
.gc-btn-sm{padding:6px 12px;font-size:10.5px}

/* ══════════════════════════════════════════════════════
   FOOTER
══════════════════════════════════════════════════════ */
.gc-footer{
  display:flex;
  align-items:center;
  justify-content:space-between;
  padding:14px 22px;
  border-top:1.5px solid var(--n4);
  background:var(--n1);
  gap:14px;
  flex-wrap:wrap;
  flex-shrink:0
}
.gc-footer-left{
  display:flex;
  align-items:center;
  gap:14px;
  flex-wrap:wrap
}
.gc-footer-right{
  display:flex;
  align-items:center;
  gap:8px;
  flex-wrap:wrap
}
.gc-footer-group{display:flex;align-items:center;gap:8px}
.gc-price-label{
  font-size:10px;
  color:var(--text-muted);
  text-transform:uppercase;
  letter-spacing:1px;
  font-weight:600;
  display:block;
  margin-bottom:2px
}
.gc-price-value{
  font-family:var(--ff-display);
  font-size:26px;
  font-weight:700;
  background:linear-gradient(135deg,var(--primary),var(--primary-light));
  -webkit-background-clip:text;
  -webkit-text-fill-color:transparent;
  background-clip:text;
  letter-spacing:1px;
  line-height:1
}
.gc-size-select{
  background:var(--n2);
  border:1.5px solid var(--n4);
  border-radius:8px;
  color:var(--text);
  font-family:var(--ff-body);
  font-size:12px;
  font-weight:600;
  padding:7px 26px 7px 12px;
  outline:none;
  appearance:none;
  cursor:pointer;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%235a7a8a' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat:no-repeat;
  background-position:right 9px center
}
.gc-qty{
  display:flex;
  align-items:center;
  border:1.5px solid var(--n4);
  border-radius:8px;
  overflow:hidden;
  background:var(--n1)
}
.gc-qty-btn{
  width:32px;height:32px;
  background:transparent;
  border:none;
  color:var(--text);
  font-size:16px;
  font-weight:600;
  cursor:pointer;
  transition:all var(--tr);
  display:flex;
  align-items:center;
  justify-content:center
}
.gc-qty-btn:hover{background:var(--primary);color:#fff}
.gc-qty-val{
  width:38px;
  text-align:center;
  font-size:14px;
  font-weight:700;
  background:var(--n2);
  line-height:32px;
  border-left:1px solid var(--n4);
  border-right:1px solid var(--n4)
}

/* ══════════════════════════════════════════════════════
   CONFIRMATION
══════════════════════════════════════════════════════ */
.gc-confirm{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  min-height:360px;
  gap:18px;
  padding:40px 20px;
  text-align:center
}
.gc-confirm-icon{
  font-size:72px;
  filter:drop-shadow(0 8px 24px rgba(52,199,89,.4));
  animation:gc-bounce .5s ease
}
@keyframes gc-bounce{
  0%{opacity:0;transform:scale(.5)}
  50%{transform:scale(1.1)}
  100%{opacity:1;transform:scale(1)}
}
.gc-confirm-title{
  font-family:var(--ff-display);
  font-size:36px;
  letter-spacing:2px;
  color:var(--emerald)
}
.gc-confirm-sub{
  font-size:13px;
  color:var(--text-muted);
  max-width:440px;
  line-height:1.6
}
.gc-confirm-summary{
  background:var(--n2);
  border:1px solid var(--n4);
  border-radius:var(--rL);
  width:100%;
  max-width:460px;
  overflow:hidden;
  margin-top:8px
}
.gc-confirm-row{
  display:flex;
  justify-content:space-between;
  padding:11px 18px;
  border-bottom:1px solid var(--n4);
  font-size:13px
}
.gc-confirm-row:last-child{border-bottom:none}
.gc-confirm-row-key{color:var(--text-muted)}
.gc-confirm-row-value{color:var(--text);font-weight:700}

/* ══════════════════════════════════════════════════════
   RESPONSIVE
══════════════════════════════════════════════════════ */
@media(max-width:900px){
  .gc-modes{grid-template-columns:1fr}
  .gc-mode{
    border-right:none;
    border-bottom:1px solid var(--n4);
    padding:12px
  }
  .gc-body{padding:16px}
  .gc-footer{flex-direction:column;align-items:stretch}
  .gc-footer-left,.gc-footer-right{justify-content:center}
}
@media(max-width:500px){
  .gc-body{padding:12px}
  .gc-inspiration-grid,.gc-ref-grid{grid-template-columns:1fr}
  .gc-price-value{font-size:22px}
  .gc-confirm-title{font-size:28px}
}
`;

/* ══════════════════════════════════════════════════════
   CONSTANTES
══════════════════════════════════════════════════════ */
const getHardcodedText = (infoSource, group, key, fallback) =>
  infoSource?.laguangeElakay?.("frontElakayHardcodedText", infoSource.languageAPI)?.[group]?.[key] || fallback;

const INSPIRATIONS = [
  { label: __elakayText("t035", "Bold & Loud"),     icon: "⚡",  desc: "Imposing letters, high impact" },
  { label: __elakayText("t036", "Clean & Modern"),  icon: "◻️", desc: "Minimal, geometric" },
  { label: __elakayText("t037", "Wild & Complex"),  icon: "🌀", desc: "Overlaps, 3D depth" },
  { label: __elakayText("t038", "Retro & Vintage"), icon: "📻", desc: "Old school, spray paint" }
];

const BUDGETS = [
  { label: __elakayText("t039", "Express ($20)"), price: 20 },
  { label: __elakayText("t040", "Standard ($35)"), price: 35 },
  { label: __elakayText("t041", "Premium ($45)"), price: 45 },
  { label: __elakayText("t042", "Pro ($60)"), price: 60 }
];
const DEFAULT_BUDGET = BUDGETS[1];
const getBudgetOption = (budget) => {
  const normalizedBudget = String(budget || "").trim().toLowerCase();
  return BUDGETS.find(({ label }) =>
    normalizedBudget.startsWith(label.split(" ")[0].toLowerCase())
  ) || DEFAULT_BUDGET;
};
const DETAIL_LEVELS = [__elakayText("t043", "Simple"), __elakayText("t044", "Moderate"), __elakayText("t045", "Detailed"), __elakayText("t046", "Hyper-detailed")];
const ARTISTIC_STYLES = [
  __elakayText("t047", "Street Art"), __elakayText("t048", "Bubble"), __elakayText("t049", "Wildstyle"), __elakayText("t050", "Calligraphy"), __elakayText("t051", "Minimalist"),
  __elakayText("t052", "Vintage"), __elakayText("t053", "Neon"), __elakayText("t054", "Abstract"), __elakayText("t055", "Cartoon"), __elakayText("t056", "Gothic")
];

const MODES_DEF = [
  { label: __elakayText("t057", "Submit Your Design"),   num: "01", icon: "🎨" },
  { label: __elakayText("t058", "Ask the Seller"),        num: "02", icon: "💬" },
  { label: __elakayText("t059", "Use Existing Graphic"),  num: "03", icon: "📋" }
];

/* ══════════════════════════════════════════════════════
   MODE 1 — Ask the Seller (formulaire)
══════════════════════════════════════════════════════ */
function AskSellerForm({ form, onChange, infoSource }) {
  __setElakayInfoSource(infoSource);
  const text = (key, fallback) => getHardcodedText(infoSource, "graffitiConfigurator", key, fallback);
  const set = (k, v) => onChange({ ...form, [k]: v });

  const toggleStyle = (s) => {
    const newPrefs = form.stylePrefs.includes(s)
      ? form.stylePrefs.filter(x => x !== s)
      : [...form.stylePrefs, s];
    set("stylePrefs", newPrefs);
  };

  const toggleInsp = (s) => {
    const newInsps = form.inspirations.includes(s)
      ? form.inspirations.filter(x => x !== s)
      : [...form.inspirations, s];
    set("inspirations", newInsps);
  };

  const fileRef = useRef();
  const [activeRef, setActiveRef] = useState(null);

  const handleFileChange = (e) => {
    if (activeRef !== null && e.target.files[0]) {
      const newRefs = form.images?.map((r, i) => i === activeRef ? e.target.files[0] : r);
      set("images", newRefs);
    }
    e.target.value = "";
  };

  return (
    <div className="gc-mode-ask">

      {/* COLONNE GAUCHE */}
      <div className="gc-mode-ask-col">

        {/* Inspirations */}
        <div className="gc-card">
          <div className="gc-card-head">
            <span className="gc-card-icon">💡</span>
            <span className="gc-card-title">{text("t001", "Style Inspiration")}</span>
          </div>
          <div className="gc-card-body">
            <div className="gc-inspiration-grid">
              {INSPIRATIONS?.map(s => (
                <div key={s.label}
                  className={`gc-inspiration-card${form.inspirations.includes(s.label) ? " on" : ""}`}
                  onClick={() => toggleInsp(s.label)}>
                  <span className="gc-inspiration-icon">{s.icon}</span>
                  <div className="gc-inspiration-info">
                    <div className="gc-inspiration-name">{s.label}</div>
                    <div className="gc-inspiration-desc">{s.desc}</div>
                  </div>
                  {form.inspirations.includes(s.label) && (
                    <span className="gc-inspiration-check">✓</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="gc-card">
          <div className="gc-card-head">
            <span className="gc-card-icon">📝</span>
            <span className="gc-card-title">{text("t002", "Special Instructions")}</span>
          </div>
          <div className="gc-card-body">

            <div className="gc-field">
              <label className="gc-label">{text("t003", "General instructions")}</label>
              <textarea className="gc-textarea" rows={3}
                value={form.specialInstructions}
                onChange={e => set("specialInstructions", e.target.value)}
                placeholder={text("t004", "Your general instructions for the artist...")} />
            </div>

            <div className="gc-field">
              <label className="gc-label">{text("t005", "Message to the artist")}</label>
              <textarea className="gc-textarea" rows={2}
                value={form.artistMessage}
                onChange={e => set("artistMessage", e.target.value)}
                placeholder={text("t006", "A personal message to the artist...")} />
            </div>

            <div className="gc-row-2">
              <div className="gc-field">
                <label className="gc-label avoid">❌ {text("t007", "Avoid")}</label>
                <textarea className="gc-textarea avoid" rows={3}
                  value={form.avoidInstructions}
                  onChange={e => set("avoidInstructions", e.target.value)}
                  placeholder={text("t024", "What you don't want...")} />
              </div>
              <div className="gc-field">
                <label className="gc-label required">✅ {text("t008", "Required")}</label>
                <textarea className="gc-textarea required" rows={3}
                  value={form.requiredInstructions}
                  onChange={e => set("requiredInstructions", e.target.value)}
                  placeholder={text("t009", "What is absolutely required...")} />
              </div>
            </div>

            <div className="gc-row-2">
              <div className="gc-field">
                <label className="gc-label">{text("t010", "Desired date")}</label>
                <input type="date" className="gc-input"
                  value={form.desiredDate}
                  onChange={e => set("desiredDate", e.target.value)}
                  min={new Date().toISOString().split("T")[0]} />
              </div>
              <div className="gc-field">
                <label className="gc-label">{text("t011", "Detail level")}</label>
                <select className="gc-select"
                  value={form.detailLevel}
                  onChange={e => set("detailLevel", e.target.value)}>
                  {DETAIL_LEVELS?.map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
            </div>

            <div className="gc-field">
              <label className="gc-label">{text("t012", "Artistic style")}</label>
              <div className="gc-chips">
                {ARTISTIC_STYLES?.map(s => (
                  <button key={s} type="button"
                    className={`gc-chip${form.stylePrefs.includes(s) ? " on" : ""}`}
                    onClick={() => toggleStyle(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className="gc-field">
              <label className="gc-label">{text("t013", "Budget")}</label>
              <div className="gc-chips">
                {BUDGETS?.map(({ label, price }) => (
                  <button key={price} type="button"
                    className={`gc-chip${form.budget === label ? " on" : ""}`}
                    onClick={() => set("budget", label)}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* COLONNE DROITE */}
      <div className="gc-mode-ask-col">

        {/* References */}
        <div className="gc-card">
          <div className="gc-card-head">
            <span className="gc-card-icon">🖼️</span>
            <span className="gc-card-title">{text("t014", "Reference Images")}</span>
          </div>
          <div className="gc-card-body">
            <div className="gc-ref-grid">
              {form.images?.map((r, i) => (
                <div key={i}
                  className={`gc-ref-slot${r ? " filled" : ""}`}
                  onClick={() => {
                    setActiveRef(i);
                    fileRef.current?.click();
                  }}>
                  {r ? (
                    <>
                      <img src={r instanceof File ? URL.createObjectURL(r) : r}
                        alt={elakayAutoText("h_f765b881ac0fd4", "Ref {value0}", { value0: i+1 })} />
                      <button type="button" className="gc-ref-remove"
                        onClick={e => {
                          e.stopPropagation();
                          set("images", form.images?.map((r2, ii) => ii === i ? null : r2));
                        }}>✕</button>
                    </>
                  ) : (
                    <>
                      <span className="gc-ref-empty-icon">+</span>
                      <span className="gc-ref-empty-label">{__elakayText("t060", "Ref")}{i+1}</span>
                    </>
                  )}
                </div>
              ))}
            </div>
            <input ref={fileRef} type="file" accept="image/*"
              style={{ display: "none" }} onChange={handleFileChange} />
          </div>
        </div>

        {/* Summary */}
        <div className="gc-card">
          <div className="gc-card-head">
            <span className="gc-card-icon">📊</span>
            <span className="gc-card-title">{text("t015", "Summary")}</span>
          </div>
          <div className="gc-card-body">
            {[
              ["Styles", form.stylePrefs.length ? form.stylePrefs.join(", ") : "—"],
              ["Inspirations", form.inspirations.length ? form.inspirations.join(", ") : "—"],
              ["Detail", form.detailLevel],
              ["Budget", form.budget],
              ["Date", form.desiredDate || "Flexible"],
              ["References", `${form.images.filter(Boolean).length}/4`]
            ]?.map(([k, v]) => (
              <div key={k} className="gc-summary-row">
                <span className="gc-summary-key">{k}</span>
                <span className="gc-summary-value">{v}</span>
              </div>
            ))}
            <div className="gc-info-banner">{__elakayText("t025", "💬 The seller will respond within 24-48h with a draft.")}</div>
          </div>
        </div>

      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MODE 3 — Choose Template (avec customisation après sélection)
══════════════════════════════════════════════════════ */
function ChooseTemplateMode({
  productID, authToken,
  infoSource,
  selectedTemplate, onSelectTemplate,
  customizationSlots, setCustomizationSlots,
  templateMeta, setTemplateMeta,
  customizedDesigns, setCustomizedDesigns
}) {
  const [previewDesign, setPreviewDesign] = useState(null);

  const handleChooseTemplate = (design) => {
    const parsed = parseLoadedDesign(design);
    if (!parsed) return;

    onSelectTemplate({
      _id: design._id,
      label: design.label,
      style: design.style,
      detail: design.detail
    });

    setCustomizationSlots(parsed.slots);
    setTemplateMeta(parsed.templateMeta);
    setCustomizedDesigns(parsed.designs);
  };

  const handlePreviewDesign = (design) => setPreviewDesign(design);

  const handleEditFromPreview = () => {
    if (!previewDesign) return;
    const designToEdit = previewDesign;
    setPreviewDesign(null);
    handleChooseTemplate(designToEdit);
  };

  /* Si template sélectionné → customisation via DesignStudio */
  if (selectedTemplate && customizationSlots.length > 0) {
    return (
      <div>
        <div className="gc-template-banner">
          <div className="gc-template-banner-icon">🎨</div>
          <div className="gc-template-banner-info">
            <div className="gc-template-banner-title">{__elakayText("t026", "Template applied:")}{selectedTemplate.label}
            </div>
            <div className="gc-template-banner-sub">{__elakayText("t027", "Customize the design below or choose another template.")}</div>
          </div>
          <button type="button" className="gc-btn gc-btn-secondary gc-btn-sm"
            onClick={() => {
              onSelectTemplate(null);
              setCustomizationSlots([]);
              setCustomizedDesigns([]);
            }}>{__elakayText("t028", "← Choose another")}</button>
        </div>

        <DesignStudio
          infoSource={infoSource}
          slots={customizationSlots}
          setSlots={setCustomizationSlots}
          templateMeta={templateMeta}
          onMetaChange={setTemplateMeta}
          initialDesigns={customizedDesigns}
          onDesignsChange={setCustomizedDesigns}
          onBack={() => {}}
          onPublish={() => {}}
          setPickerOpen={() => {}}
          setActiveSlotId={() => {}}
        />
      </div>
    );
  }

  /* Sinon → liste des templates vendeur */
  return (
    <>
      <DesignsList
        productID={productID}
        authToken={authToken}
        mode="public"
        showCreateButton={false}
        showEditButton={false}
        showDeleteButton={false}
        onPreviewDesign={handlePreviewDesign}
        onLoadDesign={handleChooseTemplate}
        topOptions={{
          status: false,
          faceCount: false,
          counters: false
        }}
        bottomOptions={{
          info: false,
          miniFaces: false,
          updatedAt: false,
          brightness: false,
          zoom: false,
          actions: true
        }}
        extraActions={(design) => (
          <button type="button" className="gc-btn gc-btn-primary"
            style={{ flex: 1, padding: "7px 10px", fontSize: 10, justifyContent: "center", minWidth: 80 }}
            onClick={() => handleChooseTemplate(design)}>{__elakayText("t029", "✓ Choose")}</button>
        )}
      />

      {previewDesign && (
        <DesignPreviewModal
          design={previewDesign}
          onClose={() => setPreviewDesign(null)}
          onEdit={handleEditFromPreview}
          showEditButton={false}
          extraActions={(design) => (
            <button type="button" className="sd-btn sd-btn-teal"
              style={{ flex: 0.3, padding: "7px 10px", fontSize: 10, justifyContent: "center", minWidth: 80 }}
              onClick={() => { handleChooseTemplate(design); setPreviewDesign(null); }}>{__elakayText("t029", "✓ Choose")}</button>
          )}
        />
      )}
    </>
  );
}

/* ══════════════════════════════════════════════════════
   COMPOSANT PRINCIPAL — GraffitiConfigurator
══════════════════════════════════════════════════════ */
export default function GraffitiConfigurator({
  productID,
  authToken,
  infoSource,
  sellerSlots = [],
  product = {
    name: __elakayText("t030", "Elakay Product"),
    price: 49.99,
    variants: ["XS", "S", "M", "L", "XL", "XXL"]
  },
  ImagesProd,
  apiUrl = null,
  initialGraphicInfo = null,
  amountTotal = 0,
  setPrixCreatorDesign = () => {},
  onAddToCart = () => {}
}) {
  const text = (key, fallback) => getHardcodedText(infoSource, "graffitiConfigurator", key, fallback);
  /* ═══════════════════════════════════════════════════════
     1️⃣  ÉTATS PRIMITIFS
  ═══════════════════════════════════════════════════════ */
  const [mode, setMode] = useState(0);
  const [variant, setVariant] = useState("M");
  const [qty, setQty] = useState(1);
  const [confirmed, setConfirmed] = useState(false);

  /* ─── Mode 0 : Custom Design ─── */
  const [customSlots, setCustomSlots] = useState(
    sellerSlots.length > 0 ? sellerSlots : [
      { id: `s${Date.now()}`, label: __elakayText("t061", "Front"), url: "", localUrl: "" }
    ]
  );
  const [customTemplateMeta, setCustomTemplateMeta] = useState({
    label: __elakayText("t031", "My Custom Order"),
    style: "Street Art",
    detail: "Moderate"
  });
  const [customDesigns, setCustomDesigns] = useState([]);
  const [restoreReady, setRestoreReady] = useState(false);
 const [pickerOpen, setPickerOpen] = useState(false);
   const [activeSlotId, setActiveSlotId] = useState(null);
 const [images, setImages] = useState("");

  /* ─── Mode 1 : Ask Seller ─── */
  const [askForm, setAskForm] = useState({
    specialInstructions: "",
    artistMessage: "",
    avoidInstructions: "",
    requiredInstructions: "",
    desiredDate: "",
    detailLevel: "Moderate",
    stylePrefs: [],
    inspirations: [],
    budget: DEFAULT_BUDGET.label,
    images: [null, null, null, null]
  });

  /* ─── Mode 2 : Template ─── */
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateCustomSlots, setTemplateCustomSlots] = useState([]);
  const [templateCustomMeta, setTemplateCustomMeta] = useState({
    label: "", style: "", detail: ""
  });
  const [templateCustomDesigns, setTemplateCustomDesigns] = useState([]);

  /* ═══════════════════════════════════════════════════════
     2️⃣  HOOK useGraphicOrderSave
  ═══════════════════════════════════════════════════════ */
  const save = useGraphicOrderSave({
    productID,
    authToken,
    apiUrl,
    infoSource,
    onCheckoutSuccess: (data) => {
      onAddToCart(data);
      setConfirmed(true);
    },
    onCheckoutError: (err) => {
      console.error("Checkout failed:", err);
    }
  });

  /* ── Composant ImageGallery (utilisé dans ImagePickerModal) ── */
  const ImageGallery = () => (
    <div className="image-gallery-section">
      <div className="section-header">
        <h4 className="section-title">{__elakayText("t001", "All Image Items")}</h4>
      </div>
      <div className="image-grid">
        {ImagesProd?.galeries?.map((nm, i) => (
          <div key={i}
            className={`image-grid-item ${images === nm.urlImg ? 'selected' : ''}`}
            onClick={() => setImages(nm.urlImg)}>
            <ShowimagesLoading src={nm.urlImg} alt={nm._id} />
          </div>
        ))}
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════════════════
     3️⃣  VALEURS DÉRIVÉES SIMPLES
  ═══════════════════════════════════════════════════════ */
  const totalPrice = useMemo(
    () => Number(amountTotal || 0).toFixed(2),
    [amountTotal]
  );

  useEffect(() => {
    const creatorDesignPrice =
      product.creatorDesign && mode === 1
        ? getBudgetOption(askForm.budget).price
        : 0;

    setPrixCreatorDesign(creatorDesignPrice);
  }, [
    askForm.budget,
    mode,
    product.creatorDesign,
    setPrixCreatorDesign
  ]);

  /* ═══════════════════════════════════════════════════════
     4️⃣  buildPayload (dépend de tout ce qui précède)
  ═══════════════════════════════════════════════════════ */
  const buildPayload = useCallback(() => {

    /* Mode 0 : Custom Design */
    if (mode === 0) {
      return {
        sourceType: "custom",
        templateMeta: customTemplateMeta,
        slots: customSlots?.map((slot, i) => {
          const d = customDesigns[i] || makeSlotDesign();
          return {
            slotId: slot.id,
            label: slot.label,
            url: slot.localUrl || slot.url || "",
            slotState: {
              texts: d.texts?.map(tb => ({ ...tb })) || [],
              layers: d.layers?.map(l => ({
                file: l.file,
                urlImg: l.src || l.url || l.urlImg || "",
                size: l.size,
                rotation: l.rotation,
                opacity: l.opacity,
                pos: { ...l.pos }
              })) || [],
              instructions: d.instructions || ""
            }
          };
        }) || [],
        variant,
        qty,
        totalPrice: parseFloat(totalPrice)
      };
    }
    /* Mode 1 : Ask Seller */
    if (mode === 1) {
      return {
        sourceType: "ask",
        askSellerData: {
          specialInstructions: askForm.specialInstructions,
          artistMessage: askForm.artistMessage,
          avoidInstructions: askForm.avoidInstructions,
          requiredInstructions: askForm.requiredInstructions,
          desiredDate: askForm.desiredDate || null,
          detailLevel: askForm.detailLevel,
          stylePrefs: askForm.stylePrefs,
          inspirations: askForm.inspirations,
          budget: askForm.budget,
          images: askForm.images
        },
        slots: [],
        variant,
        qty,
        totalPrice: parseFloat(totalPrice)
      };
    }

    /* Mode 2 : Template */
    if (mode === 2) {
      const payload = {
        sourceType: "template",
        templateRef: selectedTemplate?._id || null,
        templateMeta: {
          label: selectedTemplate?.label,
          style: selectedTemplate?.style,
          detail: selectedTemplate?.detail
        },
        variant,
        qty,
        totalPrice: parseFloat(totalPrice)
      };

      /* Inclut les overrides si customisation faite */
      if (templateCustomSlots.length > 0 && templateCustomDesigns.length > 0) {
        payload.slots = templateCustomSlots?.map((slot, i) => {
          const d = templateCustomDesigns[i] || makeSlotDesign();
          return {
            slotId: slot.id,
            label: slot.label,
            url: slot.localUrl || slot.url || "",
            slotState: {
              texts: d.texts?.map(tb => ({ ...tb })) || [],
              layers: d.layers?.map(l => ({
                file: l.file,
                urlImg: l.src || l.url || l.urlImg || "",
                size: l.size,
                rotation: l.rotation,
                opacity: l.opacity,
                pos: { ...l.pos }
              })) || [],
              instructions: d.instructions || ""
            }
          };
        });
      } else {
        payload.slots = [];
      }

      return payload;
    }

    return null;
  }, [
    mode,
    customSlots, customTemplateMeta, customDesigns,
    askForm,
    selectedTemplate, templateCustomSlots, templateCustomDesigns,
    variant, qty, totalPrice
  ]);

  /* ═══════════════════════════════════════════════════════
     5️⃣  canSubmit (dépend de buildPayload + save.validate)
  ═══════════════════════════════════════════════════════ */
  const canSubmit = useMemo(() => {
    const payload = buildPayload();
    if (!payload) return false;
      /* validation normale (sourceType, etc.) */
 return save.validate(payload).valid ?save.validate(payload).valid : false;

  /* exiger au moins un layer avec image */
  return save.hasLayerImage(payload);

    // return save.validate(payload).valid;
  }, [buildPayload, save]);

  /* ═══════════════════════════════════════════════════════
     6️⃣  EFFETS
  ═══════════════════════════════════════════════════════ */

  /* ─── Restauration depuis initialGraphicInfo ou localStorage ─── */
  useEffect(() => {
    let toRestore = initialGraphicInfo;

    if (!toRestore) {
      toRestore = save.restore();
    }

    if (!toRestore) {
      setRestoreReady(true);
      return;
    }

    const { sourceType, slots, templateRef, templateMeta, askSellerData } = toRestore;

    if (sourceType === "custom") {
      setMode(0);
      if (slots?.length > 0) {
        setCustomSlots(slots.map(s => ({
          id: s.slotId,
          label: s.label,
          url: s.url,
          localUrl: ""
        })));
        setCustomDesigns(slots.map(s => ({
          texts: s.slotState?.texts || [],
          layers: s.slotState?.layers || [],
          instructions: s.slotState?.instructions || "",
          zoom: 1,
          pan: { x: 0, y: 0 }
        })));
      }
      if (templateMeta) setCustomTemplateMeta(templateMeta);
    }

    if (sourceType === "ask" && askSellerData && product.creatorDesign) {
      setMode(1);
      setAskForm(prev => ({
        ...prev,
        ...askSellerData,
        budget: getBudgetOption(askSellerData.budget).label,
        images: [null, null, null, null]
      }));
    }

    if (sourceType === "template") {
      setMode(2);
      setSelectedTemplate({ _id: templateRef, ...templateMeta });
      if (slots?.length > 0) {
        setTemplateCustomSlots(slots.map(s => ({
          id: s.slotId,
          label: s.label,
          url: s.url,
          localUrl: ""
        })));
        setTemplateCustomDesigns(slots.map(s => ({
          texts: s.slotState?.texts || [],
          layers: s.slotState?.layers || [],
          instructions: s.slotState?.instructions || "",
          zoom: 1,
          pan: { x: 0, y: 0 }
        })));
        if (templateMeta) setTemplateCustomMeta(templateMeta);
      }
    }

    setRestoreReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── Auto-save vers localStorage à chaque changement (debounced 1.5s) ─── */
  useEffect(() => {
    if (confirmed) return;

    const payload = buildPayload();
    if (payload) {
      save.autoSaveDraft(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    customSlots, customTemplateMeta, customDesigns,
    askForm,
    selectedTemplate, templateCustomSlots, templateCustomMeta, templateCustomDesigns,
    variant, qty, confirmed
  ]);

  /* ═══════════════════════════════════════════════════════
     7️⃣  HANDLERS
  ═══════════════════════════════════════════════════════ */

  /* Bouton "💾 Save Draft" → localStorage uniquement, pas de validation stricte */
  const handleSaveDraft = async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    if (!payload) return;
    await save.saveDraft(payload, { force: true });
  };

  /* Bouton "🛒 Checkout" → validation stricte + save + notify parent */
  const handleCheckout = async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    if (!payload) return;
    await save.checkout(payload);
  };

  /* ═══════════════════════════════════════════════════════
     8️⃣  RENDER
  ═══════════════════════════════════════════════════════ */
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: CSS + (CSS_ || "") }} />
      <div className="gc-root">

        {/* ── Mode switcher ── */}
        <div
          className="gc-modes"
          style={{ gridTemplateColumns: `repeat(${product.creatorDesign ? 3 : 2}, 1fr)` }}
        >
          {MODES_DEF?.map((m, i) => (
            (i !== 1 || product.creatorDesign) && (
              <button key={i} type="button"
                className={`gc-mode${mode === i ? " on" : ""}`}
                onClick={() => {
                  setMode(i);
                  setConfirmed(false);
                  save.clearError();
                }}>
                <span className="gc-mode-num">{m.num}</span>
                <span className="gc-mode-icon">{m.icon}</span>
                {m.label}
              </button>
            )
          ))}
        </div>

        {/* ── Status bar ── */}
        {(save.busy || save.error || save.validationErrors?.length > 0 || save.savedStatus) && (
          <div className={`gc-status ${
            save.error || save.validationErrors?.length > 0 ? "error" :
            save.savedStatus === "submitted" ? "submitted" :
            save.savedStatus === "draft" ? "draft" :
            "busy"
          }`}>
            <span className="gc-status-icon">
              {save.busy ? "⏳" :
               save.validationErrors?.length > 0 ? "⚠️" :
               save.error ? "⚠️" :
               save.savedStatus === "submitted" ? "✅" :
               save.savedStatus === "draft" ? "💾" : "ℹ️"}
            </span>

            <div style={{ flex: 1, minWidth: 0 }}>
              {/* État busy */}
              {save.busy && save.busyMode === "draft" && (
                <span className="gc-status-main">{text("t016", "Saving draft...")}</span>
              )}
              {save.busy && save.busyMode === "checkout" && (
                <span className="gc-status-main">{text("t017", "Submitting...")}</span>
              )}

              {/* Validation errors */}
              {!save.busy && save.validationErrors?.length > 0 && (
                <>
                  <div className="gc-status-main">{text("t018", "Cannot continue")}</div>
                  <ul style={{
                    margin: "4px 0 0",
                    paddingLeft: 16,
                    fontSize: 11,
                    opacity: .9
                  }}>
                    {save.validationErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </>
              )}

              {/* Generic error */}
              {!save.busy && !save.validationErrors?.length && save.error && (
                <span className="gc-status-main">{save.error}</span>
              )}

              {/* Saved status */}
              {!save.busy && !save.error && save.savedStatus === "submitted" && (
                <span className="gc-status-main">{text("t019", "Order submitted!")}</span>
              )}
              {!save.busy && !save.error && save.savedStatus === "draft" && save.lastSavedAt && (
                <span className="gc-status-main">{__elakayText("t032", "Draft saved at")}{new Date(save.lastSavedAt).toLocaleTimeString()}
                </span>
              )}
            </div>

            {(save.error || save.validationErrors?.length > 0) && (
              <button type="button" className="gc-status-close"
                onClick={save.clearError}>✕</button>
            )}
          </div>
        )}

        {/* Banner si on a un draft restauré */}
        {save.hasDraft && !save.savedStatus && (
          <div className="gc-status draft">
            <span className="gc-status-icon">💾</span>
            <span className="gc-status-main">{text("t020", "Previous draft restored")}</span>
          </div>
        )}

        {/* ── Body ── */}
        <div className="gc-body">

          {confirmed ? (
            /* Confirmation screen */
            <div className="gc-confirm">
              <div className="gc-confirm-icon">✅</div>
              <div className="gc-confirm-title">{text("t021", "Order Customized!")}</div>
              <div className="gc-confirm-sub">{__elakayText("t033", "Your customization has been saved. You can now proceed with shipping and payment.")}</div>
              <div className="gc-confirm-summary">
                {[
                  ["Mode", MODES_DEF[mode].label],
                  ["Product", product.name],
                  ["Variant", `${variant} × ${qty}`],
                  ["Total", `$${totalPrice}`]
                ]?.map(([k, v]) => (
                  <div key={k} className="gc-confirm-row">
                    <span className="gc-confirm-row-key">{k}</span>
                    <span className="gc-confirm-row-value">{v}</span>
                  </div>
                ))}
              </div>
              <button type="button" className="gc-btn gc-btn-secondary"
                onClick={() => setConfirmed(false)}>{__elakayText("t034", "← Edit again")}</button>
            </div>
          ) : (
            <>
              {/* MODE 0 — Submit Your Design */}
              {mode === 0 && restoreReady && (
                <DesignStudio
                  infoSource={infoSource}
                  slots={customSlots}
                  setSlots={setCustomSlots}
                  setPickerOpen={setPickerOpen}
                  setActiveSlotId={setActiveSlotId}
                  onBack={() => {}}
                  onPublish={() => {}}
                  templateMeta={customTemplateMeta}
                  onMetaChange={setCustomTemplateMeta}
                  initialDesigns={customDesigns}
                  onDesignsChange={setCustomDesigns}
                  open={pickerOpen}
                  ImageGallery={ImageGallery}
                  images={images}
                  slotId={activeSlotId}
                />
              )}

              {/* MODE 1 — Ask the Seller */}
              {mode === 1 && product.creatorDesign && (
                <AskSellerForm form={askForm} onChange={setAskForm} infoSource={infoSource} />
              )}

              {/* MODE 2 — Use Existing Graphic */}
              {mode === 2 && (
                <ChooseTemplateMode
                  productID={productID}
                  authToken={authToken}
                  infoSource={infoSource}
                  selectedTemplate={selectedTemplate}
                  onSelectTemplate={setSelectedTemplate}
                  customizationSlots={templateCustomSlots}
                  setCustomizationSlots={setTemplateCustomSlots}
                  templateMeta={templateCustomMeta}
                  setTemplateMeta={setTemplateCustomMeta}
                  customizedDesigns={templateCustomDesigns}
                  setCustomizedDesigns={setTemplateCustomDesigns}
                />
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        {!confirmed && (
          <div className="gc-footer">

            <div className="gc-footer-left">

              <div className="gc-footer-group">
                <div>
                  <span className="gc-price-label">{text("t022", "Size")}</span>
                  <select className="gc-size-select"
                    value={variant}
                    onChange={e => setVariant(e.target.value)}>
                    {(product.variants || ["One Size"]).map(v => (
                      <option key={v}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="gc-qty">
                <button type="button" className="gc-qty-btn"
                  onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
                <span className="gc-qty-val">{qty}</span>
                <button type="button" className="gc-qty-btn"
                  onClick={() => setQty(q => q + 1)}>+</button>
              </div>

              <div>
                <span className="gc-price-label">{text("t023", "Total")}</span>
                <div className="gc-price-value">${totalPrice}</div>
              </div>

            </div>

            <div className="gc-footer-right">
              <button type="button" className="gc-btn gc-btn-ghost"
                onClick={handleSaveDraft}
                disabled={save.busy}>
                {save.busy && save.busyMode === "draft"
                  ? elakayAutoText("h_332e41e125cd6a", "⏳ Saving...")
                  : elakayAutoText("h_0ba278608b61eb", "💾 Save Draft")}
              </button>

              <button type="button" className="gc-btn gc-btn-success"
                onClick={handleCheckout}
                disabled={!canSubmit || save.busy}>
                {save.busy && save.busyMode === "checkout"
                  ? elakayAutoText("h_5f8f9ddda8f1ba", "⏳ Processing...")
                  : `🛒 Checkout · $${totalPrice}`}
              </button>
            </div>

          </div>
        )}

      </div>
    </>
  );
}
